<script setup>
import { computed, ref } from "vue";
import { STEP_CONTACT, STEP_KNOCK, STEP_MATERIAL } from "../../constants/steps";
import { useConfiguratorStore } from '../../stores/configurator';
import Form from '../Form/Form.vue';
import { useUiStore } from '../../stores/ui.js';

const configurationStore = useConfiguratorStore();
const uiStore = useUiStore();

const STEPS_HANDLED_BY_SCROLL_EVENT = [STEP_MATERIAL, STEP_KNOCK, STEP_CONTACT];

const touchStart = ref(null);
const touchStartTime = ref(null);
const lastScrollTop = ref(0);

// Info: Steps 'material', 'knocks' and 'contact' are handled differently because they contain more scrollable items
// it might be necessary to adapt the array 'stepsHandledByScrollEvent'
// and include other form views if these forms should also display more items
// relevant events for few items: touchstart, touchend, wheel
// relevant events for many items: scroll
function touchStartHandler(event) {
    if (shouldHandleWithScroll.value) return;

    touchStart.value = event.touches[0].clientY;
    touchStartTime.value = new Date().getTime();
}

function touchEndHandler(event) {
    if (shouldHandleWithScroll.value) return;

    const touchEnd = event.changedTouches[0].clientY;
    const touchEndTime = new Date().getTime();
    const speed =
        Math.abs(touchEnd - touchStart) / (touchEndTime - touchStartTime.value); // in px/ms

    if (touchStart.value > touchEnd && speed > 0.5) {
        uiStore.isScrollingUp = false;
    } else if (touchStart.value < touchEnd && speed > 0.5) {
        uiStore.isScrollingUp = true;
    }
}

function wheelHandler(event) {
    if (shouldHandleWithScroll.value) return;

    const treshold = 80;

    if (event.wheelDelta > treshold) {
        uiStore.isScrollingUp = true;
    } else if (event.wheelDelta < -treshold) {
        uiStore.isScrollingUp = false;
    }
}

function scrollHandler(el) {
    if (!shouldHandleWithScroll.value) return;

    // prevent glitching when scrolled to bottom
    if (el.offsetHeight + el.scrollTop > el.scrollHeight - 20) {
        return;
    }

    const st = el.scrollTop;
    if (st > lastScrollTop + 5) {
        uiStore.isScrollingUp = false;
    } else if (st + 5 < lastScrollTop) {
        uiStore.isScrollingUp = true;
    } // else was horizontal scroll

    lastScrollTop.value = st <= 0 ? 0 : st;
}

const shouldHandleWithScroll = computed(() => {
    return STEPS_HANDLED_BY_SCROLL_EVENT.includes(uiStore.currentStep);
});
</script>

<template>
    <div class="tw-relative tw-grow tw-bg-white">
        <div
            class="ConfiguratorScrollArea lg:tw-absolute tw-inset-0 tw-w-full tw-p-3 tw-transition-all tw-overflow-auto tw-overscroll-y-none tw-smooth-scroll tw-z-10 md:tw-p-5 xxl:tw-p-8 xxxl:tw-p-10 tw-py-4 xxl:tw-py-6"
            :class="[
                uiStore.isScrollingUp && '-tw-mt-[68px] tw-bg-white tw-z-40 lg:tw-mt-0',
                shouldHandleWithScroll
                    ? 'tw-overflow-y-auto'
                    : uiStore.isScrollingUp
                    ? 'tw-overflow-y-hidden'
                    : 'tw-overflow-y-auto',
                uiStore.currentStepIndex === 0 && '!tw-mb-0',
            ]"
            @touchstart.passive="touchStartHandler($event)"
            @touchend.passive="touchEndHandler($event)"
            @wheel.passive="wheelHandler($event)"
            @scroll="scrollHandler($event)"
        >
            <Form />
        </div>
    </div>
</template>
